import { type IdeaAttributes } from './generated/IdeaAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import Errors from '../Errors';
import Fragment from '../Fragment';
import { model, modelObject } from '../modelBuilder';
import { HasCustomFieldValues } from './concerns/HasCustomFieldValues';
import { HasRecordPositions } from './concerns/HasRecordPositions';
import type Query from '../Query';
import type Requirement from './Requirement';
import type Epic from './Epic';
import type Feature from './Feature';
import { type IdeaPromotableSubjectUnion } from './generated/types';

interface Idea
  extends IdeaAttributes,
    HasRecordPositions,
    HasCustomFieldValues {}

@model
@HasRecordPositions
@HasCustomFieldValues
class Idea extends ApplicationModel {
  // Start auto-generated
  static typename: 'Idea' = 'Idea';
  get typename(): 'Idea' {
    return 'Idea';
  }

  static internalTypename: 'Ideas::Idea' = 'Ideas::Idea';
  get internalTypename(): 'Ideas::Idea' {
    return 'Ideas::Idea';
  }

  static terminologyName = 'Idea';

  static inputType = 'IdeaAttributes!';

  static filterType = 'IdeaFilters!';
  static filters = {
    id: '[ID!]',
    projectId: 'ID',
    assignedToUserId: '[ID!]',
    promoted: 'Boolean',
    workflowStatusId: '[ID!]',
    categoryId: '[ID!]',
    portalId: '[ID!]',
    query: 'String',
  };

  static fields = {
    commentsCount: attr({ readable: true }),
    contributorshipId: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    id: attr({ readable: true }),
    ideaCategoriesCount: attr({ readable: true }),
    ideaCategorizationsCount: attr({ readable: true }),
    ideaThemeIdeasCount: attr({ readable: true }),
    ideaThemesCount: attr({ readable: true }),
    lastPortalCommentAt: attr({ readable: true }),
    name: attr({ writable: true, readable: true }),
    numEndorsements: attr({ readable: true }),
    path: attr({ readable: true }),
    portalCommentsCount: attr({ readable: true }),
    projectId: attr({ readable: true }),
    promotableId: attr({ readable: true }),
    referenceNum: attr({ readable: true }),
    score: attr({ readable: true }),
    scoringSystemId: attr({ readable: true }),
    tagList: attr({ readable: true }),
    timeInCurrentStatus: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    visibility: attr({ readable: true }),
    votes: attr({ readable: true }),
    workflowStatusId: attr({ readable: true }),
    assignedToUser: attr.belongsTo({
      writeShape: ['id', 'name', 'email'],
      readable: true,
      typename: 'User',
    }),
    contributorship: attr.belongsTo({
      readable: true,
      typename: ['IdeaUser', 'User'],
    }),
    description: attr.belongsTo({ readable: true, typename: 'Note' }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    project: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Project',
    }),
    promotable: attr.belongsTo({
      readable: true,
      typename: ['Epic', 'Feature', 'Initiative', 'Release', 'Requirement'],
    }),
    researchFolder: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Page',
    }),
    screenDefinition: attr.belongsTo({
      readable: true,
      typename: 'ScreenDefinition',
    }),
    submittedIdeaPortal: attr.belongsTo({
      readable: true,
      typename: 'IdeasIdeaPortal',
    }),
    terminology: attr.belongsTo({
      readable: true,
      typename: 'TerminologyItem',
    }),
    workflowStatus: attr.belongsTo({
      writeShape: ['id', 'name', 'category'],
      readable: true,
      typename: 'WorkflowStatus',
    }),
    addIdeaCategories: attr.hasMany({ writable: true, readable: false }),
    addIdeaThemes: attr.hasMany({ writable: true, readable: false }),
    bookmarksRecordPositions: attr.hasMany({
      args: { filters: 'RecordPositionFilters' },
      readable: true,
    }),
    childRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    comments: attr.hasMany({ readable: true }),
    customFieldValues: attr.hasMany({
      args: { ids: '[ID!]', keys: '[String!]' },
      readable: true,
    }),
    ideaCategories: attr.hasMany({ writable: true, readable: true }),
    ideaCategorizations: attr.hasMany({ readable: true }),
    ideaThemeIdeas: attr.hasMany({ readable: true }),
    ideaThemes: attr.hasMany({ writable: true, readable: true }),
    parentRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    removeIdeaCategories: attr.hasMany({ writable: true, readable: false }),
    removeIdeaThemes: attr.hasMany({ writable: true, readable: false }),
    scoreFacts: attr.hasMany({ writable: true, readable: true }),
    tags: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  /**
   * Promote idea to another record type. This will create the new record type
   * and return it.
   *
   * @example
   *   const feature = await idea.promoteTo(Feature.select('id', 'name'), {releaseId: 'WSL-R-1'});
   *   console.log(feature.name);
   *
   * @param query
   * @param options When promoting
   *   to feature or epic a releaseId must be given. For a requirement a featureId
   *   must be given.
   * @returns {Promise<Aha.IdeaPromotableSubjectUnion>}
   */
  async promoteTo(
    query: Query<any, Feature | Epic | Requirement>,
    options:
      | { projectId?: string }
      | { releaseId?: string }
      | { featureId?: string }
  ): Promise<IdeaPromotableSubjectUnion> {
    const fragment = Object.entries(options)
      .reduce(
        (fragment, [argName, value]) =>
          value ? fragment.argument(argName, value) : fragment,
        new Fragment('promoteIdea')
      )
      .argument('id', this.id)
      .argument(
        'type',
        aha.enums.IdeaPromotableTypeEnum[query.model.typename],
        true
      )
      .fragment(query.union().first().buildQueryFragment('promotable'));

    const { promoteIdea: data } = await ApplicationModel.mutate(fragment);
    const result = modelObject<
      {
        promotable: IdeaPromotableSubjectUnion;
      } & Aha.RecordErrors
    >(data);

    const errors = new Errors(result.errors);

    if (errors.isEmpty) {
      return result.promotable;
    } else {
      throw new Error(errors.fullMessages.join(', '));
    }
  }
}

export default Idea;
